import { AnalyticsDataset } from '@hap/analytics'
import { IconTypes, IconWeight } from '@hap/components'
import {
  NEGATIVE_SENTIMENT_LABEL_ID,
  NEUTRAL_SENTIMENT_LABEL_ID,
  POSITIVE_SENTIMENT_LABEL_ID,
} from '@hap/constants'

import { SavedQueryFilter } from '../saved-query'

export const DEFAULT_FILTER_OPERATORS = ['in', 'nin']

export const AVAILABLE_FILTERS: Record<
  string,
  {
    name: string
    icon: IconTypes
    iconWeight?: IconWeight
    onlyAllowOnce?: boolean
    dataset?: AnalyticsDataset
    filterCollection?: string
    suffix: string
    operators?: Array<'gte' | 'lte' | 'between' | 'in' | 'nin'>
  }
> = {
  channelId: {
    name: 'Channels',
    icon: 'vinyl-record',
    onlyAllowOnce: true,
    filterCollection: 'channels',
    suffix: 'channels',
  },
  // @deprecated use handledByIds
  // handledBy: {
  //   name: 'Handled by',
  //   icon: 'user-circle',
  //   filterCollection: 'users',
  //   suffix: 'members',
  //   onlyAllowOnce: true,
  // },
  externalId: {
    name: 'External ID',
    icon: 'cards',
    filterCollection: 'externalIds',
    suffix: 'external IDs',
    onlyAllowOnce: true,
  },
  handledByIds: {
    name: 'Handled by',
    icon: 'user-circle',
    filterCollection: 'users',
    suffix: 'members',
    onlyAllowOnce: true,
  },
  assignedSupervisorId: {
    name: 'Reports to',
    icon: 'user-circle',
    filterCollection: 'users',
    suffix: 'supervisors',
    onlyAllowOnce: true,
  },
  handleTime: {
    name: 'Duration',
    icon: 'timer',
    filterCollection: 'duration',
    suffix: 'mins',
    operators: ['gte', 'lte', 'between'],
    onlyAllowOnce: true,
  },
  wrapTime: {
    name: 'Wrap Time',
    icon: 'timer',
    filterCollection: 'duration',
    suffix: 'mins',
    operators: ['gte', 'lte', 'between'],
    onlyAllowOnce: true,
  },
  talkTime: {
    name: 'Talk Time',
    icon: 'microphone',
    filterCollection: 'duration',
    suffix: 'mins',
    operators: ['gte', 'lte', 'between'],
    onlyAllowOnce: true,
  },
  recordingDuration: {
    name: 'Recording Duration',
    icon: 'cassette-tape',
    filterCollection: 'duration',
    suffix: 'mins',
    operators: ['gte', 'lte', 'between'],
    onlyAllowOnce: true,
  },
  messageCount: {
    name: 'Messages',
    icon: 'hash',
    filterCollection: 'messageCount',
    suffix: 'messages',
    operators: ['gte', 'lte', 'between'],
    onlyAllowOnce: true,
  },
  intentId: {
    name: 'Customer Intents',
    icon: 'chat-circle-dots',
    suffix: 'intents',
    onlyAllowOnce: true,
  },
  method: {
    name: 'Contact Methods',
    icon: 'headset',
    onlyAllowOnce: true,
    suffix: 'methods',
    filterCollection: 'methods',
  },
  flagId: {
    name: 'Flag',
    icon: 'flag',
    iconWeight: 'fill',
    onlyAllowOnce: true,
    suffix: 'flags',
    filterCollection: 'flags',
  },
  tagIds: {
    name: 'Highlights',
    icon: 'bookmark-simple',
    suffix: 'highlights',
    filterCollection: 'tags',
  },
  scorecardId: {
    name: 'Scorecards',
    icon: 'medal',
    dataset: 'evaluations',
    onlyAllowOnce: true,
    filterCollection: 'scorecards',
    suffix: 'scorecards',
  },

  scorecardCriteriaId: {
    name: 'Scorecard Criteria',
    icon: 'medal',
    dataset: 'evaluationCriteriaScores',
    filterCollection: 'scorecardCriteria',
    suffix: 'criteria',
  },
  averageEvaluationScore: {
    name: 'Evaluation Score',
    icon: 'exam',
    filterCollection: 'averageEvaluationScore',
    onlyAllowOnce: true,
    operators: ['gte', 'lte', 'between'],
    suffix: 'AQS',
  },

  groupIds: {
    name: 'Groups',
    icon: 'users',
    onlyAllowOnce: true,
    filterCollection: 'groups',
    suffix: 'groups',
  },
  initialCustomerReactionIds: {
    name: 'Initial Customer Reactions',
    icon: 'smiley',
    suffix: 'Initial customer reactions',
  },
  medialCustomerReactionIds: {
    name: 'Medial Customer Reactions',
    icon: 'smiley',
    suffix: 'Medial customer reactions',
  },
  closingCustomerReactionIds: {
    name: 'Closing Customer Reactions',
    icon: 'smiley',
    suffix: 'Closing customer reactions',
  },
  piiIds: {
    name: 'PII Detections',
    icon: 'identification-badge',
    iconWeight: 'fill',
    onlyAllowOnce: true,
    filterCollection: 'pii',
    suffix: 'PII detections',
  },
  isOutbound: {
    name: 'Direction',
    icon: 'arrows-out-simple',
    onlyAllowOnce: true,
    filterCollection: 'direction',
    suffix: 'Direction',
  },
  to: {
    name: 'To address',
    icon: 'hash',
    onlyAllowOnce: true,
    suffix: 'address',
    filterCollection: 'dnis',
  },
  workflowId: {
    name: 'Workflows',
    icon: 'flow-arrow',
    onlyAllowOnce: true,
    filterCollection: 'workflow',
    suffix: 'Workflow',
  },
  workspaceId: {
    name: 'Workspaces',
    icon: 'squares-four',
    dataset: 'evaluationCriteriaScores',
    onlyAllowOnce: true,
    filterCollection: 'workspaces',
    suffix: 'workspaces',
  },
  topicIds: {
    name: 'Topics',
    icon: 'tag',
    dataset: 'evaluationCriteriaScores',
    onlyAllowOnce: true,
    suffix: 'topics',
  },

  /* Clustering Fields */
  signalIds: {
    name: 'Signals',
    icon: 'warning-circle',
    suffix: 'signals',
  },
  outcomeId: {
    name: 'Outcomes',
    icon: 'check-circle',
    onlyAllowOnce: true,
    suffix: 'outcomes',
  },
  missedOpportunityIds: {
    name: 'Missed Opportunities',
    icon: 'x-circle',
    suffix: 'missed opportunities',
  },
  qualifiedLead: {
    name: 'Qualified Lead',
    icon: 'sparkle',
    onlyAllowOnce: true,
    suffix: 'qualified lead',
    filterCollection: 'boolean',
  },

  /* Escalation Fields */

  escalationRiskScore: {
    name: 'Escalation Risk',
    icon: 'speedometer',
    onlyAllowOnce: true,
    suffix: 'risk',
    filterCollection: 'averageEvaluationScore',
    operators: ['gte', 'lte', 'between'],
  },
} as const

export const ALIAS_FIELDS = [
  'overallCustomerSentimentLabelId',
  'v2_overallSentimentId',
  'deadAirCount',
  'agentOvertalkOccurrences',
  'metHandleTimeGoal',
  'metDeadAirGoal',
  'hasAutoEvaluation',
  'hasManualEvaluation',
  'isVoicemail',
  'hasEscalation',
]

export const STATIC_FILTER_OPTIONS: {
  label: string
  icon: IconTypes
  filter: SavedQueryFilter
}[][] = [
  [
    {
      label: 'Overall positive sentiment',
      icon: 'smiley',
      filter: {
        op: 'in',
        field: 'overallCustomerSentimentLabelId',
        values: [POSITIVE_SENTIMENT_LABEL_ID],
      },
    },
    {
      label: 'Overall neutral sentiment',
      icon: 'smiley-meh',
      filter: {
        op: 'in',
        field: 'overallCustomerSentimentLabelId',
        values: [NEUTRAL_SENTIMENT_LABEL_ID],
      },
    },
    {
      label: 'Overall negative sentiment',
      icon: 'smiley-angry',
      filter: {
        op: 'in',
        field: 'overallCustomerSentimentLabelId',
        values: [NEGATIVE_SENTIMENT_LABEL_ID],
      },
    },
    {
      label: 'Overall positive sentiment (BETA)',
      icon: 'smiley',
      filter: {
        op: 'in',
        field: 'v2_overallSentimentId',
        values: [POSITIVE_SENTIMENT_LABEL_ID],
      },
    },
    {
      label: 'Overall neutral sentiment (BETA)',
      icon: 'smiley-meh',
      filter: {
        op: 'in',
        field: 'v2_overallSentimentId',
        values: [NEUTRAL_SENTIMENT_LABEL_ID],
      },
    },
    {
      label: 'Overall negative sentiment (BETA)',
      icon: 'smiley-angry',
      filter: {
        op: 'in',
        field: 'v2_overallSentimentId',
        values: [NEGATIVE_SENTIMENT_LABEL_ID],
      },
    },
  ],
  [
    {
      label: 'Is Voicemail',
      icon: 'cassette-tape',
      filter: {
        op: 'eq',
        field: 'isVoicemail',
        value: true,
      },
    },
    {
      label: 'Is Not Voicemail',
      icon: 'cassette-tape',
      filter: {
        op: 'eq',
        field: 'isVoicemail',
        value: false,
      },
    },
    {
      label: 'Has comments',
      icon: 'chat-circle-dots',
      filter: {
        op: 'isNotNull',
        field: 'lastCommentedAt',
      },
    },
    {
      label: 'Met dead air goal',
      icon: 'ear-slash',
      filter: {
        op: 'eq',
        field: 'metDeadAirGoal',
        value: true,
      },
    },
    {
      label: 'Missed dead air goal',
      icon: 'ear-slash',
      filter: {
        op: 'eq',
        field: 'metDeadAirGoal',
        value: false,
      },
    },
    {
      label: 'Has agent overtalk',
      icon: 'bird',
      filter: {
        op: 'gt',
        field: 'agentOvertalkOccurrences',
        value: '0',
      },
    },
    {
      label: 'Met handle time goal',
      icon: 'clock-countdown',
      filter: {
        op: 'eq',
        field: 'metHandleTimeGoal',
        value: true,
      },
    },
    {
      label: 'Missed handle time goal',
      icon: 'clock-countdown',
      filter: {
        op: 'eq',
        field: 'metHandleTimeGoal',
        value: false,
      },
    },
    {
      label: 'Has Escalation',
      icon: 'warning-circle',
      filter: {
        op: 'eq',
        value: true,
        field: 'hasEscalation',
        dataset: 'interactions',
      },
    },
  ],
  [
    {
      label: 'Has evaluation',
      icon: 'medal',
      filter: {
        op: 'isNotNull',
        field: 'averageEvaluationScore',
        dataset: 'interactions',
      },
    },
    {
      label: 'Has auto evaluation',
      icon: 'sparkle',
      filter: {
        op: 'eq',
        value: true,
        field: 'hasAutoEvaluation',
        dataset: 'interactions',
      },
    },
    {
      label: 'Has manual evaluation',
      icon: 'barbell',
      filter: {
        op: 'eq',
        value: true,
        field: 'hasManualEvaluation',
        dataset: 'interactions',
      },
    },
    {
      label: 'Has dispute',
      icon: 'flag',
      filter: {
        op: 'eq',
        value: true,
        field: 'hasDispute',
        dataset: 'interactions',
      },
    },
    {
      label: 'No dispute',
      icon: 'flag',
      filter: {
        op: 'eq',
        value: false,
        field: 'hasDispute',
        dataset: 'interactions',
      },
    },
    {
      label: 'Has not been evaluated',
      icon: 'medal',
      filter: {
        op: 'isNull',
        field: 'averageEvaluationScore',
        dataset: 'interactions',
      },
    },
    {
      label: 'Reviewed',
      icon: 'user-circle-check',
      filter: {
        op: 'isNotNull',
        field: 'reviewedAt',
        dataset: 'evaluations',
      },
    },
    {
      label: 'Pending reviewed',
      icon: 'tray',
      filter: {
        op: 'isNull',
        field: 'reviewedAt',
        dataset: 'evaluations',
      },
    },
  ],
]

const FILTER_FIELD_LABEL_OVERRIDES: Record<string, string> = {
  overallCustomerSentimentLabelId: 'Overall sentiment',
  v2_overallSentimentId: 'Overall sentiment (BETA)',
  isVoicemail: 'Voicemail',
  lastCommentedAt: 'Comments',
  metDeadAirGoal: 'Dead air goal',
  agentOvertalkOccurrences: 'Agent overtalk',
  metHandleTimeGoal: 'Handle time goal',
  hasEscalation: 'Escalations',
  hasAutoEvaluation: 'Auto evaluations',
  hasManualEvaluation: 'Manual evaluations',
  hasDispute: 'Disputes',
  reviewedAt: 'Review status',
}

const UNIMPLEMENTED_FILTER_FIELDS = new Set([
  'topicIds',
  'signalIds',
  'outcomeId',
  'missedOpportunityIds',
  'qualifiedLead',
])

export const FILTER_CONTROL_FIELDS = [
  ...Object.keys(AVAILABLE_FILTERS).filter((field) => {
    return !UNIMPLEMENTED_FILTER_FIELDS.has(field)
  }),
  ...STATIC_FILTER_OPTIONS.flat().map(({ filter }) => filter.field),
].filter((field, index, fields) => fields.indexOf(field) === index)

export const FILTER_FIELD_OPTIONS = [
  ...Object.entries(AVAILABLE_FILTERS)
    .filter(([field]) => FILTER_CONTROL_FIELDS.includes(field))
    .map(([field, filter]) => ({
      field,
      label: filter.name,
    })),
  ...STATIC_FILTER_OPTIONS.flat().map(({ filter, label }) => ({
    field: filter.field,
    label: FILTER_FIELD_LABEL_OVERRIDES[filter.field] || label,
  })),
  { field: 'assignedTo', label: 'Assigned evaluator' },
]
  .filter((option, index, options) => {
    return options.findIndex(({ field }) => field === option.field) === index
  })
  .sort((a, b) => a.label.localeCompare(b.label))
